import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type PointerEvent } from 'react'
import type { MeasurementPoint } from '../types/domain'
import { number } from './format'
import { convertPressureToForceKgf } from './pressureUnits'

const COLORS = ['#e63c2f', '#34373c', '#2f80ed', '#27ae60', '#9b51e0', '#d97706', '#00a6a6', '#6b7c93']
const DEFAULT_SENSOR_AREA_CM2 = 1
type TimeSeriesUnit = 'KPA' | 'KGF'

const unitLabel: Record<TimeSeriesUnit, string> = { KPA: 'кПа', KGF: 'кгс' }

export function TimeSeriesChart({ points, palletNumber }: { points: MeasurementPoint[]; palletNumber?: string }) {
  const stageRef = useRef<HTMLDivElement>(null)
  const [stageWidth, setStageWidth] = useState(900)
  const [selectedTime, setSelectedTime] = useState<number>()
  const [sensorSearch, setSensorSearch] = useState('')
  const [unit, setUnit] = useState<TimeSeriesUnit>('KPA')
  const [sensorAreaInput, setSensorAreaInput] = useState(String(DEFAULT_SENSOR_AREA_CM2))
  const sensorAreaCm2 = validArea(sensorAreaInput)
  const sensors = useMemo(() => Array.from(new Map(points.map(point => [point.sensorId, {
    id: point.sensorId,
    serial: point.sensorSerial,
  }])).values()), [points])
  const [selectedSensors, setSelectedSensors] = useState<string[]>(() => sensors.slice(0, 4).map(sensor => sensor.id))
  const matchingSensors = useMemo(() => {
    const query = sensorSearch.trim().toLocaleLowerCase('ru-RU')
    return query ? sensors.filter(sensor => sensor.serial.toLocaleLowerCase('ru-RU').includes(query)) : sensors
  }, [sensorSearch, sensors])

  useEffect(() => {
    setSelectedSensors(current => {
      const available = new Set(sensors.map(sensor => sensor.id))
      const retained = current.filter(id => available.has(id))
      return retained.length ? retained : sensors.slice(0, 4).map(sensor => sensor.id)
    })
  }, [sensors])

  useEffect(() => {
    const stage = stageRef.current
    if (!stage) return
    const update = () => setStageWidth(Math.max(280, Math.round(stage.clientWidth)))
    update()
    const observer = new ResizeObserver(update)
    observer.observe(stage)
    return () => observer.disconnect()
  }, [])

  const visiblePoints = useMemo(() => {
    const selected = new Set(selectedSensors)
    return points.filter(point => selected.has(point.sensorId))
  }, [points, selectedSensors])
  const chart = useMemo(() => visiblePoints.length ? buildChart(visiblePoints, stageWidth, unit, sensorAreaCm2) : undefined,
    [visiblePoints, stageWidth, unit, sensorAreaCm2])

  if (!points.length) return <div className="empty-chart">Для выбранной паллеты пока нет измерений.</div>

  const selected = selectedTime === undefined ? undefined : chart?.atTime(selectedTime)

  function toggleSensor(sensorId: string) {
    setSelectedSensors(current => current.includes(sensorId) ? current.filter(id => id !== sensorId) : [...current, sensorId])
  }

  function selectMatchingSensors() {
    const matchingIds = new Set(matchingSensors.map(sensor => sensor.id))
    setSelectedSensors(current => [...new Set([...current, ...matchingIds])])
  }

  function moveCursor(event: PointerEvent<SVGRectElement>) {
    if (!chart) return
    const bounds = event.currentTarget.getBoundingClientRect()
    const ratio = Math.max(0, Math.min(1, (event.clientX - bounds.left) / Math.max(1, bounds.width)))
    setSelectedTime(chart.nearestTime(chart.plotLeft + ratio * chart.plotWidth))
  }

  function moveByKeyboard(event: KeyboardEvent<SVGRectElement>) {
    if (!chart || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return
    event.preventDefault()
    const currentIndex = selectedTime === undefined ? chart.times.length - 1 : Math.max(0, chart.times.indexOf(selectedTime))
    const nextIndex = event.key === 'Home' ? 0 : event.key === 'End' ? chart.times.length - 1
      : Math.max(0, Math.min(chart.times.length - 1, currentIndex + (event.key === 'ArrowLeft' ? -1 : 1)))
    setSelectedTime(chart.times[nextIndex])
  }

  function downloadSelected() {
    const csv = buildTimeSeriesCsv(visiblePoints, sensorAreaCm2)
    const url = URL.createObjectURL(new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' }))
    const link = document.createElement('a')
    link.href = url
    link.download = `${palletNumber ?? 'паллета'}-время-выбранные-датчики.csv`
    link.click()
    window.setTimeout(() => URL.revokeObjectURL(url), 0)
  }

  return (
    <div className="chart-wrap">
      <section className="time-series-toolbar" aria-label="Настройка датчиков на графике">
        <div className="time-series-toolbar-heading">
          <div><span className="eyebrow">SENSOR FILTER</span><h3>Датчики на графике</h3></div>
          <strong>{selectedSensors.length} из {sensors.length}</strong>
        </div>
        <label className="time-sensor-search">
          <span>Поиск по номеру датчика</span>
          <input type="search" value={sensorSearch} onChange={event => setSensorSearch(event.currentTarget.value)} placeholder="Например, NR-S-0001" />
        </label>
        <div className="time-sensor-picker">
          {matchingSensors.map(sensor => <label key={sensor.id}>
            <input type="checkbox" checked={selectedSensors.includes(sensor.id)} onChange={() => toggleSensor(sensor.id)} />
            <span>{sensor.serial}</span>
          </label>)}
          {!matchingSensors.length && <span className="time-sensor-empty">Датчики не найдены</span>}
        </div>
        <div className="time-unit-control">
          <div className="unit-switch" role="group" aria-label="Единицы временного графика">
            <button type="button" className={unit === 'KPA' ? 'active' : ''} onClick={() => setUnit('KPA')}>кПа</button>
            <button type="button" className={unit === 'KGF' ? 'active' : ''} onClick={() => setUnit('KGF')}>кгс</button>
          </div>
          <label><span>Площадь узла, см²</span><input aria-label="Площадь измерительного узла" type="number" inputMode="decimal" min="0.01" max="10000" step="0.1" value={sensorAreaInput} onChange={event => setSensorAreaInput(event.currentTarget.value)} onBlur={() => setSensorAreaInput(String(sensorAreaCm2))} /></label>
        </div>
        <div className="time-series-actions">
          <button type="button" className="button secondary" disabled={!matchingSensors.length} onClick={selectMatchingSensors}>{sensorSearch ? 'Выбрать найденные' : 'Выбрать все'}</button>
          <button type="button" className="button secondary" onClick={() => setSelectedSensors([])}>Снять выбор</button>
          <button type="button" className="button primary" disabled={!visiblePoints.length} onClick={downloadSelected}>Скачать CSV · {selectedSensors.length}</button>
        </div>
        <p>Сила рассчитывается как давление × площадь узла. CSV содержит только выбранные датчики, кПа, площадь и кгс.</p>
      </section>

      {!chart ? <div className="empty-chart">Выберите хотя бы один датчик для отображения.</div> : <>
        <div className="chart-stage" ref={stageRef}>
          <svg viewBox={`0 0 ${chart.width} ${chart.height}`} role="img" aria-label="График давления выбранных датчиков во времени с интерактивным курсором">
            <rect x={chart.plotLeft} y={chart.plotTop} width={chart.plotWidth} height={chart.plotHeight} rx="8" fill="#f8fafc" />
            {chart.ticks.map(tick => (
              <g key={tick.value}>
                <line x1={chart.plotLeft} x2={chart.plotRight} y1={tick.y} y2={tick.y} stroke="#dce4ec" strokeDasharray="4 6" />
                <text x={chart.plotLeft - 8} y={tick.y + 4} textAnchor="end" className="chart-label">{number(tick.value, unit === 'KPA' ? 1 : 2)}</text>
              </g>
            ))}
            {chart.series.map((series, index) => (
              <path data-testid="time-series-line" key={series.id} d={series.path} fill="none" stroke={COLORS[index % COLORS.length]} strokeWidth="2.4" strokeLinejoin="round" opacity="0.9" />
            ))}
            <line x1={chart.plotLeft} x2={chart.plotRight} y1={chart.plotBottom} y2={chart.plotBottom} stroke="#9fb3c8" />
            <text x={chart.plotLeft} y={chart.height - 24} className="chart-label">{chart.from}</text>
            <text x={chart.plotRight} y={chart.height - 24} textAnchor="end" className="chart-label">{chart.to}</text>

            {selected && <g className="chart-cursor" pointerEvents="none">
              <line x1={selected.x} x2={selected.x} y1={chart.plotTop} y2={chart.plotBottom} stroke="#102a43" strokeWidth="1.2" strokeDasharray="3 4" />
              {selected.values.map(value => <circle key={value.id} cx={selected.x} cy={value.y} r="4.5"
                fill={COLORS[value.colorIndex % COLORS.length]} stroke="#fff" strokeWidth="2" />)}
              {chart.showTooltip && <>
                <rect x={selected.tooltipX} y={chart.plotTop + 8} width="240" height={46 + selected.values.length * 16} rx="9" fill="#102a43" opacity="0.96" />
                <text x={selected.tooltipX + 12} y={chart.plotTop + 27} className="chart-tooltip-time">{chart.exactFormat.format(selected.time)}</text>
                {selected.values.map((value, index) => <g key={value.id}>
                  <circle cx={selected.tooltipX + 15} cy={chart.plotTop + 47 + index * 16} r="3" fill={COLORS[value.colorIndex % COLORS.length]} />
                  <text x={selected.tooltipX + 24} y={chart.plotTop + 50 + index * 16} className="chart-tooltip-label">{value.label}</text>
                  <text x={selected.tooltipX + 228} y={chart.plotTop + 50 + index * 16} textAnchor="end" className="chart-tooltip-value">{number(value.value, unit === 'KPA' ? 1 : 2)} {unitLabel[unit]}</text>
                </g>)}
              </>}
              <rect x={Math.max(chart.plotLeft, Math.min(chart.plotRight - 94, selected.x - 47))} y={chart.plotBottom + 8} width="94" height="20" rx="5" fill="#102a43" />
              <text x={Math.max(chart.plotLeft + 47, Math.min(chart.plotRight - 47, selected.x))} y={chart.plotBottom + 22} textAnchor="middle" className="chart-tooltip-time">{chart.timeFormat.format(selected.time)}</text>
            </g>}

            <rect className="chart-hit-area" x={chart.plotLeft} y={chart.plotTop} width={chart.plotWidth} height={chart.plotHeight} fill="transparent" tabIndex={0}
              role="slider" aria-label="Время на графике" aria-valuemin={chart.times[0]} aria-valuemax={chart.times.at(-1)}
              aria-valuenow={selected?.time ?? chart.times.at(-1)} aria-valuetext={selected ? chart.exactFormat.format(selected.time) : 'Последнее измерение'}
              onPointerDown={moveCursor} onPointerMove={moveCursor}
              onPointerLeave={event => { if (event.pointerType === 'mouse') setSelectedTime(undefined) }}
              onFocus={() => setSelectedTime(current => current ?? chart.times.at(-1))} onKeyDown={moveByKeyboard} />
          </svg>
        </div>
        <p className="chart-unit">{unit === 'KPA' ? 'Давление' : 'Сила'}, {unitLabel[unit]}</p>
        <div className="chart-legend">
          {chart.series.map((series, index) => <span key={series.id}><i style={{ background: COLORS[index % COLORS.length] }} />{series.label}</span>)}
        </div>
        <div className="chart-hover-summary" aria-live="polite">
          {selected ? <><strong>{chart.exactFormat.format(selected.time)}</strong><div>{selected.values.map(value => <span key={value.id}><i style={{ background: COLORS[value.colorIndex % COLORS.length] }} />{value.label}<b>{number(value.value, unit === 'KPA' ? 1 : 2)} {unitLabel[unit]}</b></span>)}</div></>
            : <span>Коснитесь графика или наведите курсор, чтобы увидеть точное время и показания выбранных датчиков.</span>}
        </div>
      </>}
    </div>
  )
}

function buildChart(points: MeasurementPoint[], requestedWidth: number, unit: TimeSeriesUnit, sensorAreaCm2: number) {
  const width = Math.max(280, requestedWidth)
  const compact = width < 560
  const height = compact ? 270 : 340
  const plotLeft = compact ? 52 : 72
  const plotRight = width - (compact ? 10 : 20)
  const plotTop = 20
  const plotBottom = height - 70
  const plotWidth = plotRight - plotLeft
  const plotHeight = plotBottom - plotTop
  const times = [...new Set(points.map(point => new Date(point.measuredAt).getTime()))].sort((a, b) => a - b)
  const values = points.map(point => displayValue(point.valueKpa, unit, sensorAreaCm2))
  const minTime = times[0] ?? 0
  const maxTime = times.at(-1) ?? minTime
  const rawMin = Math.min(...values)
  const rawMax = Math.max(...values)
  const padding = Math.max(unit === 'KPA' ? 1 : .05, (rawMax - rawMin) * 0.12)
  const minValue = Math.max(0, rawMin - padding)
  const maxValue = rawMax + padding
  const x = (time: number) => plotLeft + ((time - minTime) / Math.max(1, maxTime - minTime)) * plotWidth
  const y = (value: number) => plotBottom - ((value - minValue) / Math.max(0.000001, maxValue - minValue)) * plotHeight
  const grouped = new Map<string, MeasurementPoint[]>()
  for (const point of points) {
    const seriesId = `${point.sensorId}:${point.channel}`
    grouped.set(seriesId, [...(grouped.get(seriesId) ?? []), point])
  }
  const series = Array.from(grouped, ([id, sensorPoints]) => {
    const sorted = sensorPoints.sort((a, b) => a.measuredAt.localeCompare(b.measuredAt))
    const first = sorted[0]
    const label = `${first?.sensorSerial ?? id} · грань ${channelLabel(first?.channel ?? 0)}`
    return {
      id,
      label,
      points: sorted,
      path: sorted.map((point, index) => `${index ? 'L' : 'M'}${x(new Date(point.measuredAt).getTime()).toFixed(1)},${y(displayValue(point.valueKpa, unit, sensorAreaCm2)).toFixed(1)}`).join(' '),
    }
  })
  const tickCount = compact ? 4 : 5
  const ticks = Array.from({ length: tickCount }, (_, index) => {
    const value = minValue + (maxValue - minValue) * index / (tickCount - 1)
    return { value, y: y(value) }
  })
  const rangeFormat = new Intl.DateTimeFormat('ru-RU', { day: '2-digit', month: compact ? '2-digit' : 'short', hour: '2-digit', minute: '2-digit' })
  const exactFormat = new Intl.DateTimeFormat('ru-RU', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' })
  const timeFormat = new Intl.DateTimeFormat('ru-RU', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
  return {
    width, height, plotLeft, plotRight, plotTop, plotBottom, plotWidth, plotHeight,
    series, ticks, times, exactFormat, timeFormat, showTooltip: !compact,
    from: rangeFormat.format(minTime), to: rangeFormat.format(maxTime),
    nearestTime: (chartX: number) => times.reduce((nearest, time) => Math.abs(x(time) - chartX) < Math.abs(x(nearest) - chartX) ? time : nearest, times[0] ?? 0),
    atTime: (time: number) => {
      const cursorX = x(time)
      const cursorValues = series.flatMap((entry, colorIndex) => {
        const point = entry.points.find(value => new Date(value.measuredAt).getTime() === time)
        const value = point ? displayValue(point.valueKpa, unit, sensorAreaCm2) : undefined
        return point && value !== undefined ? [{ id: entry.id, label: entry.label, value, y: y(value), colorIndex }] : []
      })
      return { time, x: cursorX, values: cursorValues, tooltipX: cursorX > width - 280 ? cursorX - 252 : cursorX + 12 }
    },
  }
}

export function buildTimeSeriesCsv(points: MeasurementPoint[], sensorAreaCm2 = DEFAULT_SENSOR_AREA_CM2) {
  const header = ['sensor', 'channel', 'plane', 'measured_at', 'pressure_kpa', 'sensor_area_cm2', 'force_kgf', 'quality']
  const rows = [...points]
    .sort((left, right) => left.measuredAt.localeCompare(right.measuredAt) || left.sensorSerial.localeCompare(right.sensorSerial) || left.channel - right.channel)
    .map(point => [
      point.sensorSerial,
      String(point.channel),
      channelLabel(point.channel),
      point.measuredAt,
      point.valueKpa.toFixed(3),
      sensorAreaCm2.toFixed(3),
      convertPressureToForceKgf(point.valueKpa, sensorAreaCm2).toFixed(6),
      point.quality,
    ].join(';'))
  return [header.join(';'), ...rows].join('\n')
}

function displayValue(valueKpa: number, unit: TimeSeriesUnit, sensorAreaCm2: number) {
  return unit === 'KPA' ? valueKpa : convertPressureToForceKgf(valueKpa, sensorAreaCm2)
}

function validArea(value: string) {
  if (!value.trim()) return DEFAULT_SENSOR_AREA_CM2
  const area = Number(value)
  return Number.isFinite(area) ? Math.min(10_000, Math.max(0.01, area)) : DEFAULT_SENSOR_AREA_CM2
}

function channelLabel(channel: number) {
  return channel === 0 ? 'A' : channel === 1 ? 'B' : String(channel + 1)
}
